import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  gradients,
  shadows,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getInputStyle,
} from '../theme';

interface DecisionOption {
  value: string;
  label: string;
  icon: string;
  description: string;
  premium?: boolean;
}

const DECISION_TYPES: Record<string, DecisionOption[]> = {
  personal: [
    { value: 'marriage', label: 'Marriage', icon: '\u2661', description: 'Finding the right time for commitment' },
    { value: 'children', label: 'Children', icon: '\u2606', description: 'Timing for family planning' },
    { value: 'moving', label: 'Moving', icon: '\u2302', description: 'Best time to relocate' },
    { value: 'travel', label: 'Travel', icon: '\u2708', description: 'Optimal travel dates' },
    { value: 'pet', label: 'Pet Adoption', icon: '\u{1F43E}', description: 'When to welcome a new pet' }
  ],
  professional: [
    { value: 'career', label: 'Career Change', icon: '\u2605', description: 'Job transitions and promotions', premium: true },
    { value: 'business', label: 'Business Launch', icon: '\u25C8', description: 'Starting a new venture', premium: true },
    { value: 'investment', label: 'Investment', icon: '\u25B3', description: 'Financial decisions', premium: true },
    { value: 'purchase', label: 'Major Purchase', icon: '\u2666', description: 'Big buying decisions', premium: true }
  ],
  health: [
    { value: 'exercise', label: 'Exercise Program', icon: '\u2665', description: 'Starting fitness routines' },
    { value: 'medical', label: 'Medical Procedure', icon: '\u2624', description: 'Scheduling treatments' },
    { value: 'development', label: 'Personal Development', icon: '\u2605', description: 'Learning and growth' }
  ]
};

const CalculatePage: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState('free');
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState('');

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    decision_type: '',
    timeframe_start: '',
    timeframe_end: '',
    priority_level: 'flexible'
  });

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchUserStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Session expired');
      const data = await response.json();
      setSubscriptionStatus(data.subscription_status);
    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCalculating(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/calculations/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          decision_type: formData.decision_type,
          timeframe_start: new Date(formData.timeframe_start).toISOString(),
          timeframe_end: new Date(formData.timeframe_end).toISOString(),
          priority_level: formData.priority_level
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Calculation failed');
      }

      navigate(`/results/${data.data.calculation_id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCalculating(false);
    }
  };

  const isPremium = subscriptionStatus === 'premium' || subscriptionStatus === 'trial';

  if (loading) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.starWhite,
        fontFamily: typography.fontFamily.heading,
        fontSize: typography.fontSize.xl,
        letterSpacing: typography.letterSpacing.wider,
      }}>
        <span style={{ color: colors.accent.gold, marginRight: '12px' }}>★</span>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.2), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.5,
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{ maxWidth: '800px', margin: '0 auto 30px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Link to="/dashboard" style={{
          color: colors.text.secondary,
          textDecoration: 'none',
          fontSize: typography.fontSize.sm,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          ← Back to Dashboard
        </Link>
      </div>

      <div style={{
        ...getCardStyle('default'),
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            fontSize: '56px',
            marginBottom: '16px',
            color: colors.accent.gold,
            textShadow: `0 0 30px rgba(212, 175, 55, 0.5)`,
          }}>◈</div>
          <h1 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.text.starWhite,
            margin: '0 0 12px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>New Calculation</h1>
          <p style={{ color: colors.text.secondary, margin: 0, fontSize: typography.fontSize.base }}>
            Select a decision type and timeframe to find your optimal timing.
          </p>
        </div>

        {error && (
          <div style={{
            background: colors.status.errorBg,
            color: colors.status.error,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '24px',
            border: `1px solid rgba(248, 113, 113, 0.3)`,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Category Selection */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              color: colors.accent.gold,
              marginBottom: '16px',
              fontFamily: typography.fontFamily.heading,
              letterSpacing: typography.letterSpacing.wide,
            }}>
              <span style={{
                width: '32px',
                height: '32px',
                background: gradients.goldButton,
                borderRadius: borderRadius.full,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.background.midnight,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold,
              }}>1</span>
              Select Category
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {['personal', 'professional', 'health'].map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(category);
                    setFormData({ ...formData, decision_type: '' });
                  }}
                  style={{
                    padding: '18px 16px',
                    borderRadius: borderRadius.md,
                    border: selectedCategory === category
                      ? `2px solid ${colors.accent.gold}`
                      : `1px solid ${colors.border.subtle}`,
                    background: selectedCategory === category
                      ? 'rgba(212, 175, 55, 0.15)'
                      : 'rgba(74, 63, 138, 0.1)',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                    fontWeight: typography.fontWeight.semibold,
                    color: selectedCategory === category ? colors.accent.gold : colors.text.secondary,
                    fontFamily: typography.fontFamily.body,
                    transition: 'all 0.3s ease',
                    boxShadow: selectedCategory === category ? shadows.goldGlow : 'none',
                  }}
                >
                  <span style={{ display: 'block', fontSize: '24px', marginBottom: '8px' }}>
                    {category === 'personal' && '\u2661'}
                    {category === 'professional' && '\u2605'}
                    {category === 'health' && '\u2665'}
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: category }} />
                  {category === 'professional' && !isPremium && (
                    <span style={{
                      display: 'block',
                      fontSize: typography.fontSize.xs,
                      color: colors.status.warning,
                      marginTop: '4px',
                    }}>Premium</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Decision Type Selection */}
          {selectedCategory && (
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: colors.accent.gold,
                marginBottom: '16px',
                fontFamily: typography.fontFamily.heading,
                letterSpacing: typography.letterSpacing.wide,
              }}>
                <span style={{
                  width: '32px',
                  height: '32px',
                  background: gradients.goldButton,
                  borderRadius: borderRadius.full,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.background.midnight,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.bold,
                }}>2</span>
                Select Decision Type
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                {DECISION_TYPES[selectedCategory as keyof typeof DECISION_TYPES].map((type) => {
                  const isLocked = type.premium && !isPremium;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      disabled={isLocked}
                      onClick={() => setFormData({ ...formData, decision_type: type.value })}
                      style={{
                        padding: '20px 16px',
                        borderRadius: borderRadius.md,
                        border: formData.decision_type === type.value
                          ? `2px solid ${colors.accent.gold}`
                          : `1px solid ${colors.border.subtle}`,
                        background: isLocked
                          ? 'rgba(26, 26, 62, 0.5)'
                          : formData.decision_type === type.value
                            ? 'rgba(212, 175, 55, 0.15)'
                            : 'rgba(74, 63, 138, 0.1)',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.5 : 1,
                        textAlign: 'left',
                        transition: 'all 0.3s ease',
                        boxShadow: formData.decision_type === type.value ? shadows.goldGlow : 'none',
                      }}
                    >
                      <div style={{
                        fontSize: '28px',
                        marginBottom: '10px',
                        color: formData.decision_type === type.value ? colors.accent.gold : colors.primary.lavender,
                      }} dangerouslySetInnerHTML={{ __html: type.icon }} />
                      <div style={{
                        fontWeight: typography.fontWeight.semibold,
                        color: formData.decision_type === type.value ? colors.accent.gold : colors.text.starWhite,
                        marginBottom: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        {type.label}
                        {isLocked && <span style={{ fontSize: typography.fontSize.xs }}>{'\u{1F512}'}</span>}
                      </div>
                      <div style={{ fontSize: typography.fontSize.xs, color: colors.text.muted }}>{type.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Timeframe Selection */}
          {formData.decision_type && (
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: colors.accent.gold,
                marginBottom: '16px',
                fontFamily: typography.fontFamily.heading,
                letterSpacing: typography.letterSpacing.wide,
              }}>
                <span style={{
                  width: '32px',
                  height: '32px',
                  background: gradients.goldButton,
                  borderRadius: borderRadius.full,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.background.midnight,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.bold,
                }}>3</span>
                Select Timeframe
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: typography.fontSize.sm,
                    color: colors.text.secondary,
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.timeframe_start}
                    onChange={(e) => setFormData({ ...formData, timeframe_start: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    style={getInputStyle('dark')}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: typography.fontSize.sm,
                    color: colors.text.secondary,
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.timeframe_end}
                    onChange={(e) => setFormData({ ...formData, timeframe_end: e.target.value })}
                    min={formData.timeframe_start || new Date().toISOString().split('T')[0]}
                    required
                    style={getInputStyle('dark')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Priority Level */}
          {formData.timeframe_start && formData.timeframe_end && (
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: colors.accent.gold,
                marginBottom: '16px',
                fontFamily: typography.fontFamily.heading,
                letterSpacing: typography.letterSpacing.wide,
              }}>
                <span style={{
                  width: '32px',
                  height: '32px',
                  background: gradients.goldButton,
                  borderRadius: borderRadius.full,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.background.midnight,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.bold,
                }}>4</span>
                Priority Level
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {[
                  { value: 'urgent', label: 'Urgent', desc: 'Need to decide soon' },
                  { value: 'flexible', label: 'Flexible', desc: 'Can wait for best timing' },
                  { value: 'optimal', label: 'Optimal Only', desc: 'Only show best dates' }
                ].map((priority) => (
                  <button
                    key={priority.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, priority_level: priority.value })}
                    style={{
                      padding: '16px',
                      borderRadius: borderRadius.md,
                      border: formData.priority_level === priority.value
                        ? `2px solid ${colors.accent.gold}`
                        : `1px solid ${colors.border.subtle}`,
                      background: formData.priority_level === priority.value
                        ? 'rgba(212, 175, 55, 0.15)'
                        : 'rgba(74, 63, 138, 0.1)',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      boxShadow: formData.priority_level === priority.value ? shadows.goldGlow : 'none',
                    }}
                  >
                    <div style={{
                      fontWeight: typography.fontWeight.semibold,
                      color: formData.priority_level === priority.value ? colors.accent.gold : colors.text.starWhite,
                    }}>{priority.label}</div>
                    <div style={{ fontSize: typography.fontSize.xs, color: colors.text.muted, marginTop: '4px' }}>{priority.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          {formData.timeframe_start && formData.timeframe_end && (
            <button
              type="submit"
              disabled={calculating || !formData.decision_type}
              style={{
                ...getButtonStyle('gold'),
                width: '100%',
                padding: '18px',
                fontSize: typography.fontSize.lg,
                opacity: calculating ? 0.7 : 1,
                cursor: calculating ? 'not-allowed' : 'pointer',
              }}
            >
              {calculating ? '\u25C8 Calculating...' : '\u2605 Calculate Optimal Timing'}
            </button>
          )}
        </form>

        {!isPremium && (
          <div style={{
            marginTop: '28px',
            padding: '18px',
            background: 'rgba(212, 175, 55, 0.1)',
            borderRadius: borderRadius.md,
            textAlign: 'center',
            border: `1px solid ${colors.border.gold}`,
          }}>
            <span style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>
★ <Link to="/subscription" style={{ color: colors.accent.gold, fontWeight: typography.fontWeight.semibold, textDecoration: 'none' }}>Upgrade to Premium</Link> for access to all decision types and unlimited calculations!
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatePage;
